/**
 * Service for managing browser notifications in PWA
 * Handles permission requests, sending notifications, and background notifications
 */
export class NotificationService {
  static instance = null;

  constructor() {
    if (NotificationService.instance) {
      return NotificationService.instance;
    }

    this.isSupported = 'Notification' in window;
    this.permission = this.isSupported ? Notification.permission : 'denied';

    NotificationService.instance = this;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      console.log('Notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Check if notifications are permitted
   */
  isPermitted() {
    return this.isSupported && this.permission === 'granted';
  }

  /**
   * Show a browser notification
   */
  async showNotification(title, options = {}) {
    if (!this.isPermitted()) {
      console.warn('Notifications not permitted');
      return null;
    }

    try {
      // Default notification options
      const defaultOptions = {
        icon: '/icon-192x192.png',
        badge: '/icon-96x96.png',
        vibrate: [200, 100, 200],
        requireInteraction: true,
        silent: false,
        tag: 'alarm-notification',
        ...options
      };

      // If service worker is available, use it for better background handling
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const registration = await navigator.serviceWorker.ready;
        return registration.showNotification(title, defaultOptions);
      } else {
        // Fallback to regular notification
        return new Notification(title, defaultOptions);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Show alarm notification with specific styling
   */
  async showAlarmNotification(alarmData) {
    const { alarmName, roomNumber, bedId, priority, color } = alarmData;
    
    const title = `🚨 ${alarmName}`;
    
    const options = {
      icon: '/icon-192x192.png',
      badge: '/icon-96x96.png',
      vibrate: this.getVibrationPattern(priority),
      requireInteraction: true,
      silent: false,
      tag: `alarm-${bedId}`,
      data: {
        type: 'alarm',
        bedId,
        roomNumber,
        alarmName,
        priority,
        timestamp: Date.now()
      },
      actions: [
        {
          action: 'view',
          title: 'View Patients',
          icon: '/icon-96x96.png'
        },
        {
          action: 'dismiss',
          title: 'Dismiss',
          icon: '/icon-96x96.png'
        }
      ]
    };

    return this.showNotification(title, options);
  }

  /**
   * Show SOS notification
   */
  async showSOSNotification(sosData) {
    const title = '🆘 SOS EMERGENCY';
    const body = `Emergency alert - Immediate attention required`;
    
    const options = {
      body,
      icon: '/icon-192x192.png',
      badge: '/icon-96x96.png',
      vibrate: [300, 100, 300, 100, 300],
      requireInteraction: true,
      silent: false,
      tag: 'sos-emergency',
      data: {
        type: 'sos',
        timestamp: Date.now(),
        ...sosData
      },
      actions: [
        {
          action: 'view',
          title: 'Open App',
          icon: '/icon-96x96.png'
        }
      ]
    };

    return this.showNotification(title, options);
  }

  /**
   * Get vibration pattern based on priority
   */
  getVibrationPattern(priority) {
    switch (priority) {
      case 1:
        return [300, 100, 300, 100, 300];
      case 2:
        return [200, 100, 200];
      case 3:
        return [200];
      default:
        return [200, 100, 200];
    }
  }

  /**
   * Clear all notifications with a specific tag
   */
  async clearNotifications(tag) {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const notifications = await registration.getNotifications({ tag });
        notifications.forEach(notification => notification.close());
      } catch (error) {
        console.error('Error clearing notifications:', error);
      }
    }
  }

  /**
   * Clear all alarm notifications
   */
  async clearAllAlarmNotifications() {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const notifications = await registration.getNotifications();
        notifications.forEach(notification => {
          if (notification.tag && notification.tag.startsWith('alarm-')) {
            notification.close();
          }
        });
      } catch (error) {
        console.error('Error clearing alarm notifications:', error);
      }
    }
  }

  /**
   * Setup notification click handlers
   */
  setupNotificationHandlers() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
          const { action, notificationData } = event.data;
          this.handleNotificationAction(action, notificationData);
        }
      });
    }
  }

  /**
   * Handle notification actions
   */
  handleNotificationAction(action, data) {
    console.log('Notification action:', action, data);

    switch (action) {
      case 'view':
        // Focus the app window and navigate to patients
        if (window.location.pathname !== '/my-patients') {
          window.history.pushState({}, '', '/my-patients');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
        window.focus();
        break;

      case 'dismiss':
        // Handle dismiss action
        if (data.type === 'alarm') {
          this.clearNotifications(`alarm-${data.bedId}`);
        }
        break;
    }
  }
}

// Create singleton instance
export const notificationService = new NotificationService();